/**
 * ConfigDraft (patch-based) service helpers.
 *
 * These endpoints operate on patch drafts stored server-side (ConfigDraft table),
 * and should be preferred for "revert" actions to avoid snapshot shape drift.
 */
export async function deleteSegmentsDraft(params: {
  iseEndpoint: string
  jazelId: string
  token: string
}): Promise<void> {
  const base = String(params.iseEndpoint || '').replace(/\/+$/g, '')
  const jazelId = String(params.jazelId || '').trim()
  const token = String(params.token || '').trim()

  if (!base) throw new Error('Missing ISE backend URL')
  if (!jazelId) throw new Error('Missing jazelId')
  if (!token) throw new Error('Missing session token')

  const url = new URL(`${base}/drafts/segments`)
  url.searchParams.set('jazelId', jazelId)

  const resp = await fetch(url.toString(), {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })

  if (!resp.ok) {
    const text = await resp.text().catch(() => '')
    throw new Error(text || `Failed to revert Groups draft (${resp.status})`)
  }
}

export async function deleteSegmentDraftPatch(params: {
  iseEndpoint: string
  jazelId: string
  segmentId: string
  token: string
}): Promise<void> {
  const base = String(params.iseEndpoint || '').replace(/\/+$/g, '')
  const jazelId = String(params.jazelId || '').trim()
  const segmentId = String(params.segmentId || '').trim()
  const token = String(params.token || '').trim()

  if (!base) throw new Error('Missing ISE backend URL')
  if (!jazelId) throw new Error('Missing jazelId')
  if (!segmentId) throw new Error('Missing segmentId')
  if (!token) throw new Error('Missing session token')

  const url = `${base}/drafts/segments/${encodeURIComponent(jazelId)}/segments/${encodeURIComponent(segmentId)}`

  const resp = await fetch(url, {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })

  if (!resp.ok) {
    const text = await resp.text().catch(() => '')
    throw new Error(text || `Failed to revert segment ${segmentId} (${resp.status})`)
  }
}

/**
 * Delete a specific listing rule op (v3 per-op revert).
 */
export async function deleteListingRuleOp(params: {
  iseEndpoint: string
  jazelId: string
  segmentId: string
  opId: string
  token: string
}): Promise<void> {
  const base = String(params.iseEndpoint || '').replace(/\/+$/g, '')
  const jazelId = String(params.jazelId || '').trim()
  const segmentId = String(params.segmentId || '').trim()
  const opId = String(params.opId || '').trim()
  const token = String(params.token || '').trim()

  if (!base) throw new Error('Missing ISE backend URL')
  if (!jazelId) throw new Error('Missing jazelId')
  if (!segmentId) throw new Error('Missing segmentId')
  if (!opId) throw new Error('Missing opId')
  if (!token) throw new Error('Missing session token')

  const url = `${base}/drafts/listing-rules/${encodeURIComponent(jazelId)}/segments/${encodeURIComponent(segmentId)}/ops/${encodeURIComponent(opId)}`

  const resp = await fetch(url, {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })

  if (!resp.ok) {
    const text = await resp.text().catch(() => '')
    throw new Error(text || `Failed to revert listing rule op ${opId} (${resp.status})`)
  }
}

/**
 * Delete entire publish rules draft (section revert).
 */
export async function deleteListingRulesDraft(params: {
  iseEndpoint: string
  jazelId: string
  token: string
}): Promise<void> {
  const base = String(params.iseEndpoint || '').replace(/\/+$/g, '')
  const jazelId = String(params.jazelId || '').trim()
  const token = String(params.token || '').trim()

  if (!base) throw new Error('Missing ISE backend URL')
  if (!jazelId) throw new Error('Missing jazelId')
  if (!token) throw new Error('Missing session token')

  const url = `${base}/drafts/listing-rules?jazelId=${encodeURIComponent(jazelId)}`

  const resp = await fetch(url, {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })

  if (!resp.ok) {
    const text = await resp.text().catch(() => '')
    throw new Error(text || `Failed to revert publish rules draft (${resp.status})`)
  }
}

/**
 * Delete a specific field within a segment for v3 field mapping patches.
 */
export async function deleteFieldMappingV3Field(params: {
  iseEndpoint: string
  jazelId: string
  segmentId: string
  fieldKey: string
  token: string
}): Promise<void> {
  const base = String(params.iseEndpoint || '').replace(/\/+$/g, '')
  const jazelId = String(params.jazelId || '').trim()
  const segmentId = String(params.segmentId || '').trim()
  const fieldKey = String(params.fieldKey || '').trim()
  const token = String(params.token || '').trim()

  if (!base) throw new Error('Missing ISE backend URL')
  if (!jazelId) throw new Error('Missing jazelId')
  if (!segmentId) throw new Error('Missing segmentId')
  if (!fieldKey) throw new Error('Missing fieldKey')
  if (!token) throw new Error('Missing session token')

  const url = `${base}/drafts/field-mapping/${encodeURIComponent(jazelId)}/segments/${encodeURIComponent(segmentId)}/fields/${encodeURIComponent(fieldKey)}`

  const resp = await fetch(url, {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })

  if (!resp.ok) {
    const text = await resp.text().catch(() => '')
    throw new Error(text || `Failed to revert field ${fieldKey} in segment ${segmentId} (${resp.status})`)
  }
}

/**
 * Delete all fields for a specific segment in v3 field mapping patches.
 */
export async function deleteFieldMappingV3Segment(params: {
  iseEndpoint: string
  jazelId: string
  segmentId: string
  token: string
}): Promise<void> {
  const base = String(params.iseEndpoint || '').replace(/\/+$/g, '')
  const jazelId = String(params.jazelId || '').trim()
  const segmentId = String(params.segmentId || '').trim()
  const token = String(params.token || '').trim()

  if (!base) throw new Error('Missing ISE backend URL')
  if (!jazelId) throw new Error('Missing jazelId')
  if (!segmentId) throw new Error('Missing segmentId')
  if (!token) throw new Error('Missing session token')

  const url = `${base}/drafts/field-mapping/${encodeURIComponent(jazelId)}/segments/${encodeURIComponent(segmentId)}`

  const resp = await fetch(url, {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })

  if (!resp.ok) {
    const text = await resp.text().catch(() => '')
    throw new Error(text || `Failed to revert all fields for segment ${segmentId} (${resp.status})`)
  }
}

export type DraftDomain = 'field-mapping' | 'segments' | 'listing-rules'

export type PatchDraftV2Like = {
  version?: number
  domain?: string
  revision?: string | number | null
  draftRevision?: string | number | null
  patches?: Record<string, any>
  updatedAt?: string
  updatedBy?: string
  updatedByUserId?: string
} | null

async function fetchDraftDomain(params: {
  iseEndpoint: string
  jazelId: string
  token: string
  domain: DraftDomain
}): Promise<PatchDraftV2Like> {
  const base = String(params.iseEndpoint || '').replace(/\/+$/g, '')
  const jazelId = String(params.jazelId || '').trim()
  const token = String(params.token || '').trim()
  const domain = params.domain

  if (!base) throw new Error('Missing ISE backend URL')
  if (!jazelId) throw new Error('Missing jazelId')
  if (!token) throw new Error('Missing session token')

  const url = new URL(`${base}/drafts/${domain}`)
  url.searchParams.set('jazelId', jazelId)

  const resp = await fetch(url.toString(), {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })

  if (!resp.ok) {
    const text = await resp.text().catch(() => '')
    throw new Error(text || `Failed to load draft ${domain} (${resp.status})`)
  }

  const json: any = await resp.json()
  return (json?.data as any) ?? null
}

export async function fetchSegmentsDraft(params: {
  iseEndpoint: string
  jazelId: string
  token: string
}): Promise<PatchDraftV2Like> {
  return await fetchDraftDomain({ ...params, domain: 'segments' })
}

export async function fetchFieldMappingDraft(params: {
  iseEndpoint: string
  jazelId: string
  token: string
}): Promise<PatchDraftV2Like> {
  return await fetchDraftDomain({ ...params, domain: 'field-mapping' })
}

export async function fetchListingRulesDraft(params: {
  iseEndpoint: string
  jazelId: string
  token: string
}): Promise<PatchDraftV2Like> {
  return await fetchDraftDomain({ ...params, domain: 'listing-rules' })
}

